import React, { useState, useEffect } from 'react';
import config from '../Config';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { centsToDollarInputString, dollarsStringToCents, formatCentsAsCurrency } from '../Utils/money';

const InsuranceSlabs = () => {
  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    start_price: '',
    end_price: '',
    amount: ''
  });

  const fetchSlabs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/insurance-slabs`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSlabs(data);
      } else {
        throw new Error('Failed to fetch insurance slabs');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlabs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingId 
        ? `${config.apiUrl}/api/v1/admin/insurance-slabs/${editingId}`
        : `${config.apiUrl}/api/v1/admin/insurance-slabs`;
      
      const startCents = dollarsStringToCents(formData.start_price);
      const endCents = dollarsStringToCents(formData.end_price);
      const amountCents = dollarsStringToCents(formData.amount);
      if (Number.isNaN(startCents) || Number.isNaN(endCents) || Number.isNaN(amountCents)) {
        toast.error('Enter valid dollar amounts');
        return;
      }
      const apiData = {
        start_price: startCents,
        end_price: endCents,
        amount: amountCents,
      };
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        toast.success(`Insurance slab ${editingId ? 'updated' : 'created'} successfully`);
        setShowModal(false);
        resetForm();
        fetchSlabs();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process insurance slab');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slab?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/insurance-slabs/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Insurance slab deleted successfully');
        fetchSlabs();
      } else {
        throw new Error('Failed to delete insurance slab');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      start_price: '',
      end_price: '',
      amount: ''
    });
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Insurance Slabs</h2>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 w-full sm:w-auto shrink-0"
            >
              Add Insurance Slab
            </button>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Price ($)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Price ($)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount ($)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {slabs.map((slab) => (
                    <tr key={slab.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-left">{formatCentsAsCurrency(slab.start_price)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">{formatCentsAsCurrency(slab.end_price)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">{formatCentsAsCurrency(slab.amount)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setEditingId(slab.id);
                            setFormData({
                              start_price: centsToDollarInputString(slab.start_price),
                              end_price: centsToDollarInputString(slab.end_price),
                              amount: centsToDollarInputString(slab.amount),
                            });
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(slab.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 z-[120] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      {editingId ? 'Edit Insurance Slab' : 'Add Insurance Slab'}
                    </h3>
                    <form onSubmit={handleSubmit}>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Start Price ($)</label>
                          <input
                            type="number"
                            name="start_price"
                            value={formData.start_price}
                            onChange={handleInputChange}
                            required
                            step="0.01"
                            min="0"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">End Price ($)</label>
                          <input
                            type="number"
                            name="end_price"
                            value={formData.end_price}
                            onChange={handleInputChange}
                            required
                            step="0.01"
                            min="0"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount ($)</label>
                          <input
                            type="number"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            required
                            step="0.01"
                            min="0"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowModal(false);
                            resetForm();
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600"
                        >
                          {editingId ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceSlabs; 